// 🎯 4단계 등급 시스템 타입 정의
// 최고 수준의 코드 품질과 유지보수성을 위한 체계적 설계

/**
 * 브랜드 타입
 */
export type Brand = 'Apple' | 'Samsung' | 'LG';

/**
 * 기기 등급 타입 (A: 최고, B: 양호, C: 보통, D: 불량, E: 최저)
 */
export type DeviceGrade = 'A' | 'B' | 'C' | 'D' | 'E';

/**
 * 등급별 가격 정보
 */
export interface GradePriceInfo {
  A: number | null;  // 최고 등급 가격
  B: number | null;  // 양호 등급 가격
  C: number | null;  // 보통 등급 가격
  D: number | null;  // 불량 등급 가격
  E: number | null;  // 최저 등급 가격
}

/**
 * 기기 모델 + 용량별 가격 테이블
 */
export interface DeviceModelPriceTable {
  deviceType: string;
  deviceModel: string;
  storage: string;
  prices: GradePriceInfo;
}

/**
 * 기기 타입별 가격 테이블 (모든 모델과 용량 포함)
 */
export interface DeviceTypePriceTable {
  deviceType: string;
  deviceModels: DeviceModelPriceTable[];
}

/**
 * 등급별 색상 정보 (접근성 고려)
 */
export interface GradeColorInfo {
  A: string;  // Green 계열
  B: string;  // Blue 계열
  C: string;  // Orange 계열
  D: string;  // Red 계열
  E: string;  // Gray 계열
}

/**
 * 등급 기준 정보
 */
export interface GradeCriteria {
  id: string;
  grade: DeviceGrade;
  title: string;                    // "기능 정상, 외관 흠집 또는 잔상 없음"
  description: string;              // 상세 설명 (2-3줄)
  checklist: string[];              // 체크리스트 항목들
  color: string;                   // 색상 코드
  icon: string;                    // 아이콘 이름
  createdAt: Date;
  updatedAt: Date;
}

/**
 * 등급별 예시 이미지 정보
 */
export interface GradeImage {
  id: string;
  grade: DeviceGrade;
  deviceType: string;              // "스마트폰", "태블릿", "에어팟"
  imageUrl: string;               // 이미지 URL
  description: string;            // 이미지 설명
  damagePoints?: Array<{          // 손상 부위 표시 (원형 포인터)
    x: number;                    // X 좌표 (퍼센트)
    y: number;                    // Y 좌표 (퍼센트)
    description: string;          // 손상 설명
  }>;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * 기기 가격 테이블 (Firestore 컬렉션)
 */
export interface DevicePriceTable {
  id: string;
  deviceType: string;             // "스마트폰", "태블릿", "에어팟"
  deviceModel: string;           // "iPhone 14 Pro"
  storage: string;               // "128GB", "256GB", "512GB"
  gradePrices: GradePriceInfo;   // 등급별 가격
  isActive: boolean;             // 활성화 여부
  version: 'draft' | 'live';     // 초안/배포 버전
  createdAt: Date;
  updatedAt: Date;
  updatedBy: string;             // 관리자 ID
  memo?: string;                 // 메모
}

/**
 * 사용자 선택 등급 정보
 */
export interface UserGradeSelection {
  deviceId: string;
  deviceType: string;
  deviceModel: string;
  storage: string;
  userSelectedGrade: DeviceGrade | null;
  estimatedPrice: number | null;  // 사용자 선택 등급 기준 예상 가격
  adminFinalGrade?: DeviceGrade | null;  // 관리자 최종 등급
  finalPrice?: number | null;    // 관리자 최종 가격
  createdAt: Date;
  updatedAt: Date;
}

/**
 * 가격 조회 요청
 */
export interface PriceLookupRequest {
  deviceType: string;
  deviceModel: string;
  storage?: string;
}

/**
 * 가격 조회 응답
 */
export interface PriceLookupResponse {
  success: boolean;
  data?: {
    deviceType: string;
    deviceModel: string;
    storage: string;
    gradePrices: GradePriceInfo;
    lastUpdated: string;
  };
  error?: string;
}

/**
 * 일괄 가격 업데이트 요청
 */
export interface BulkPriceUpdateRequest {
  updates: Array<{
    id: string;
    gradePrices: Partial<GradePriceInfo>;
  }>;
  version: 'draft' | 'live';
}

/**
 * 일괄 가격 업데이트 응답
 */
export interface BulkPriceUpdateResponse {
  success: boolean;
  updatedCount: number;
  errors: Array<{
    id: string;
    error: string;
  }>;
}

/**
 * 가격 검증 결과
 */
export interface PriceValidationResult {
  isValid: boolean;
  error?: string;
  warnings?: string[];
}

/**
 * 등급 선택 상태
 */
export interface GradeSelectionState {
  selectedGrade: DeviceGrade | null;
  gradePrices: GradePriceInfo | null;
  gradeCriteria: GradeCriteria[];
  gradeImages: GradeImage[];
  isLoading: boolean;
  error: string | null;
}

/**
 * 등급 선택 액션 타입
 */
export type GradeSelectionAction = 
  | { type: 'SET_SELECTED_GRADE'; payload: DeviceGrade | null }
  | { type: 'SET_GRADE_PRICES'; payload: GradePriceInfo }
  | { type: 'SET_GRADE_CRITERIA'; payload: GradeCriteria[] }
  | { type: 'SET_GRADE_IMAGES'; payload: GradeImage[] }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'RESET_SELECTION' };

/**
 * 등급별 상수 정의
 */
export const GRADE_CONSTANTS = {
  COLORS: {
    A: '#10b981',  // Green-500
    B: '#3b82f6', // Blue-500
    C: '#f59e0b', // Orange-500
    D: '#ef4444', // Red-500
    E: '#6b7280', // Gray-500
  } as GradeColorInfo,
  
  LABELS: {
    A: '최고 등급',
    B: '양호 등급',
    C: '보통 등급',
    D: '불량 등급',
    E: '최저 등급',
  },
  
  DESCRIPTIONS: {
    A: '기능 정상, 외관 흠집 또는 잔상 없음',
    B: '기능 정상, 생활기스·미세 흠집 또는 약한 잔상',
    C: '기능 정상, 뚜렷한 흠집·찍힘 또는 중잔상',
    D: 'LCD 불량, 전원, 카메라, Face ID 등 주요 기능 불량',
    E: '전원 불가, 침수, 부품용 등 정상 사용이 불가능한 상태',
  },
  
  CHECKLISTS: {
    A: ['액정 상태 양호', '후면 상태 양호', '잔상 없음', 'LCD 정상', '전원 정상', '카메라 정상', 'Face ID 정상', '나침반 정상', '음성녹음 정상'] as string[],
    B: ['액정 미세 흠집', '후면 생활기스', '약한 잔상', 'LCD 정상', '전원 정상', '카메라 정상', 'Face ID 정상', '나침반 정상', '음성녹음 정상'] as string[],
    C: ['액정 뚜렷한 흠집', '후면 찍힘', '중잔상', 'LCD 정상', '전원 정상', '카메라 정상', 'Face ID 정상', '나침반 정상', '음성녹음 정상'] as string[],
    D: ['액정 파손/크랙', '후면 심각한 손상', '심한 잔상', 'LCD 불량', '전원 불량', '카메라 불량', 'Face ID 불량', '나침반 불량', '음성녹음 불량'] as string[],
    E: ['액정 심각한 파손 또는 없음', '후면 심각한 손상, 몸체 휨/파손', '잔상 확인 불가', 'LCD 확인 불가 또는 불량', '전원 불가 또는 불안정', '카메라 불량 또는 확인 불가', 'Face ID 불량 또는 확인 불가', '침수 이력 있음', '부품용으로만 활용 가능'] as string[],
  },
} as const;

/**
 * 가격 검증 규칙
 */
export const PRICE_VALIDATION_RULES = {
  MIN_PRICE: 0,
  MAX_PRICE: 10000000,
  MONOTONICITY: true, // A ≥ B ≥ C ≥ D ≥ E
} as const;
